import { newFeatureFilter, newPropertyFilter } from '../request-filters';
import { isFeatureClickable } from '../features';
import {  boundingBoxIsLikePoint } from '../bounding-box';
import { newBoundingBoxEvent } from '../bounding-box';
import { whenFeaturesAvailable } from '../features';
import MaxZoomLevels from '../zoom-levels';
import { deactiveFeaturesForSource, filterLayerIfExists, removeApproxLocationCircle } from '../layers';
import LocationFeature from '../models/feature';

import {
    Coast,
    District,
    Entity,
    Municipality,
    Neighbourhood,
    Province,
    Region,
} from '../../locations';

const handleEntityLayerClick = async function( ctx, event ) {
    const mapFeature = event.detail.feature;

    const isAlreadySelected = ( ctx.currentLocation && ctx.currentLocation.id && ctx.currentLocation.id === mapFeature.id );

    if ( !mapFeature || isAlreadySelected ) {
        return;
    }

    if ( ctx.abortController ) {
        ctx.abortController.abort();
        ctx.abortController = new AbortController();
    }

    const feature = LocationFeature.fromMapBoxFeature( mapFeature );

    if ( !isFeatureClickable( feature ) ) {
        return;
    }

    ctx.setLoading( true );

    ctx.movefuncs = [];

    ctx.root.dispatchEvent( new CustomEvent( 'twc:collapse:collapse-all', { bubbles: true } ) );

    ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:search-this-area:deactivate', { bubbles: true } ) );

    ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:draw-your-search:deactivate', {
        bubbles: true,
        detail: {
            search: false,
        },
    } ) );

    ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:remove-area', {
        bubbles: true,
        detail: {
            search: false,
        },
    } ) );

    removeApproxLocationCircle( ctx.map );

    ctx.locationIndicator.update( feature.label( ctx.language ), {
        autohide: true,
        timeout: 5000,
    } );

    let fitBounds = false;

    let currentZoom = ctx.map.getZoom();

    if ( currentZoom < 12 && ctx.options.fitBoundsOnClick ) {
        fitBounds = true;
    }

    let featureParams = newFeatureFilter( {
        parentType: feature.type,
        parentID: feature.id,
    } );

    if ( ctx.currentLocation !== null ) {
        ctx.previousLocation = ctx.currentLocation;
    }

    ctx.currentLocation = mapFeature;

    let locationKeys = document.querySelectorAll( '.property-search-form [name=locationId]' );
    for ( let k = 0; k < locationKeys.length; k++ ) {
        locationKeys[ k ].dataset.latitude = feature.centerLatitude;
        locationKeys[ k ].dataset.longitude = feature.centerLongitude;
    }

    const propertyParams = newPropertyFilter( {}, ctx.baseForm );
    propertyParams.set( 'currentLocationID', feature.id );
    propertyParams.set( 'currentLocationType', Entity );
    propertyParams.set( 'locationBoundSW', feature.bboxSouthWest );
    propertyParams.set( 'locationBoundNE', feature.bboxNorthEast );
    propertyParams.set( 'latitude', feature.centerLatitude );
    propertyParams.set( 'longitude', feature.centerLongitude );

    if ( feature.municipalityID && feature.municipalityID.length > 0 ) {
        for ( let i = 0; i < feature.municipalityID.length; i++ ) {
            propertyParams.append( 'parentLocations', Municipality + ':' + feature.municipalityID[ i ] );
        }
    }

    let metaOptions = {
        focusMapToSearch: fitBounds,
        tasks: [],
        appendFeaturesIfLayerExists: false,
        fetchPropertiesAfterAnimation: fitBounds,
    };

    let featureTypeToLoad = feature.nextChildFeatureType();

    if ( !propertyParams.has( 'radius' ) && fitBounds ) {
        let bboxOptions = newBoundingBoxEvent();

        if ( boundingBoxIsLikePoint( feature.bbox ) && feature.type in MaxZoomLevels ) {
            bboxOptions.maxZoom = MaxZoomLevels[ feature.type ];
        }

        const bboxEvent = newBoundingBoxEvent();

        metaOptions.fitBounds = {
            bbox: feature.bbox,
            options: bboxOptions,
            event: bboxEvent,
        };
    }

    ctx.map.setFeatureState(
        {
            source: Entity,
            id: feature.id,
        },
        {
            active: true,
        } );

    const hasLayerToFilter = ctx.map.getLayer( `${feature.type}:fill` );

    if ( featureTypeToLoad && featureTypeToLoad !== Entity && hasLayerToFilter ) {
        filterLayerIfExists( ctx.map, `${feature.type}:fill`, [ '!=', [ 'get', 'id' ], feature.id ], ctx.debug );
    } else if ( hasLayerToFilter ) {
        filterLayerIfExists( ctx.map, `${feature.type}:fill`, null, ctx.debug );
    }

    const regionFilter = [ '!=', [ 'get', 'id' ], feature.regionID ];

    filterLayerIfExists( ctx.map, `${Region}:fill`, regionFilter, ctx.debug );
    filterLayerIfExists( ctx.map, `${Region}:outline`, regionFilter, ctx.debug );

    const provinceFilterIDs = [
        feature.regionID,
    ];

    if ( feature.islandID > 0 ) {
        provinceFilterIDs.push( feature.islandID );
    } else {
        provinceFilterIDs.push( feature.provinceID );
    }

    const provinceFilter = [
        '!', [
            'match',
            [ 'get', 'id' ],
            provinceFilterIDs,
            true,
            false,
        ],
    ];

    filterLayerIfExists( ctx.map, `${Province}:fill`, provinceFilter, ctx.debug );
    filterLayerIfExists( ctx.map, `${Province}:outline`, provinceFilter, ctx.debug );

    deactiveFeaturesForSource( ctx.map, Neighbourhood );

    deactiveFeaturesForSource( ctx.map, District );

    deactiveFeaturesForSource( ctx.map, Province );

    let entitySource = ctx.map.getSource( Entity );

    if ( entitySource ) {
        let entityFeatures = entitySource.serialize().data.features || [];

        entityFeatures = entityFeatures.filter( function( e ) {
            return parseInt( e.id ) !== feature.id;
        } );

        for ( let e = 0; e < entityFeatures.length; e++ ) {
            ctx.map.setFeatureState(
                {
                    source: Entity,
                    id: entityFeatures[ e ].id,
                },
                {
                    active: false,
                } );
        }
    }

    let municipalitySource = ctx.map.getSource( Municipality );

    if ( municipalitySource ) {
        let municipalityFeatures = municipalitySource.serialize().data.features || [];

        if ( featureTypeToLoad === Entity || !featureTypeToLoad ) {
            municipalityFeatures = municipalityFeatures.filter( function( m ) {
                return parseInt( m.id ) !== feature.id;
            } );
        }

        if ( featureTypeToLoad === Entity || !featureTypeToLoad ) {
            if ( ctx.map.getLayer( Municipality ) ) {
                ctx.map.setFilter( Municipality, null );
            }

            ctx.map.setFeatureState(
                {
                    source: Municipality,
                    id: feature.id,
                },
                {
                    active: true,
                } );
        }

        for ( let i = 0; i < municipalityFeatures.length; i++ ) {
            ctx.map.setFeatureState(
                {
                    source: Municipality,
                    id: municipalityFeatures[ i ].id,
                },
                {
                    active: false,
                } );
        }
    }

    let featurePath = featureTypeToLoad;

    if  ( !featurePath ) {
        featurePath = Municipality;
    }

    let options = {
        fetchFeatures: true,
        featureSearchURL: new URL( `/${featurePath}`, ctx.apiURL ),
        featureSearchParams: featureParams,
        fetchProperties: true,
        propertySearchURL: ctx.propertySearchURL,
        propertySearchParams: propertyParams,
    };

    metaOptions.appendFeaturesIfLayerExists = true;

    if ( ( !featureTypeToLoad && featureTypeToLoad !== Entity ) || ( featureTypeToLoad && featureTypeToLoad === Entity ) ) {
        if ( ctx.map.getSource( feature.type ) ) {
            ctx.map.setFeatureState(
                {
                    source: feature.type,
                    id: feature.id,
                },
                {
                    active: true,
                } );
        }
    }

    if ( featureTypeToLoad && featureTypeToLoad !== Entity ) {
        whenFeaturesAvailable( ctx.map, {
            source: featureTypeToLoad,
            filter: null,
            validate: ctx.debug,
        }, ctx.abortController.signal ).then( ( features ) => {
            for ( let i = 0; i < features.length; i++ ) {
                ctx.map.setFeatureState(
                    {
                        source: featureTypeToLoad,
                        id: features[ i ].id,
                    },
                    {
                        active: true,
                    } );
            }
        } );

        ctx.movefuncs = [ ( ctx, feature ) => {
            if ( feature.source !== featureTypeToLoad ) {
                return;
            }

            const featureType = ctx.currentLocation.properties.type;
            const currentLocationID = ctx.currentLocation.properties.id;
            const featureCoastID = feature.coastID;

            if ( featureType === Coast && featureCoastID !== currentLocationID ) {
                return;
            }

            if ( ctx.map.getSource( featureTypeToLoad ) ) {
                const filter = {
                    source: featureTypeToLoad,
                    id: feature.id,
                };

                const state = {
                    active: true,
                };

                ctx.map.setFeatureState( filter, state );
            }
        } ];
    }

    let results = [];

    try {
        results = await ctx.update( options, metaOptions, ctx.abortController.signal );
    } catch ( err ) {
        if ( err.name === 'AbortError' ) {
            return;
        }
    }

    if ( metaOptions.tasks.length > 0 ) {
        let tasks = [];

        for ( let i = 0; i < metaOptions.tasks.length; i++ ) {
            tasks = [ ...tasks, metaOptions.tasks[ i ] ];
        }

        for ( let t = 0; t < tasks.length; t++ ) {
            const task = tasks[ t ];

            try {
                const result = await task();
                results = [ ...results, ...result ];
            } catch ( err ) {
                if ( err.name === 'AbortError' ) {
                    return;
                }
            }
        }
    }

    ctx.previousSearchState = propertyParams.toString();

    ctx.setLoading( false );

    return results;
};

export {
    handleEntityLayerClick,
};
